import React, { useCallback, useState } from "react";
import { ScriptLoader } from "components/ScriptLoader";
import { FullPageSpinner } from "components/Spinner";
import { isNilOrEmpty, useSessionJWT, useSessionCtx, noop } from "utils";
import { useApi } from "utils/useApi";
import { logInfo, getSettingsRebilly, getAppSettings } from "api/accounts-customer";
import RebillyForm from "./Form";

// TODO: This is a duplicate of web/src/utils/settingsContext.js
const useAppSettings = () => {
  const { sessionJWT } = useSessionJWT();
  return useApi(useCallback(() => getAppSettings(sessionJWT), [sessionJWT]));
};

const finalErrors = (rebillySettings, appSettings) => {
  if (appSettings.error) return appSettings.error;
  if (rebillySettings.error) return rebillySettings.error;
  return undefined;
};

const finalData = (rebillySettings, appSettings) => {
  let result = {};
  if (appSettings.data) {
    result = { ...result, ...appSettings.data };
  }
  if (rebillySettings.data) {
    result = { ...result, ...rebillySettings.data };
  }
  return isNilOrEmpty(result) ? undefined : { ...result };
};

const useSettings = () => {
  const { sessionJWT } = useSessionJWT();

  const rebillySettings = useApi(useCallback(() => getSettingsRebilly(sessionJWT), [sessionJWT]));
  const appSettings = useAppSettings();

  return {
    error: finalErrors(rebillySettings, appSettings),
    loading: rebillySettings.loading || appSettings.loading,
    data: finalData(rebillySettings, appSettings),
  };
};

export const Rebilly = ({
  Loader = ScriptLoader,
  onReady = noop,
  onComplete,
  onCancel,
  config = {},
  hideCancelButton = false,
  // Must reach the form rather than stop at this surface: an unconditional hook call in InnerForm
  // would gate the out-of-scope render paths (Chooser, /addcard) too.
  requireTOS = false,
}) => {
  const { setFlowState, debug } = useSessionCtx();
  const { sessionJWT } = useSessionJWT();
  const [scriptLoaded, setScriptLoaded] = useState(false);

  const settings = useSettings();

  if (settings.error) {
    debug(settings.error);
    return <div>ERROR</div>;
  }
  if (settings.loading) {
    return <FullPageSpinner />;
  }

  const showSpinner = settings.loading || !scriptLoaded;
  const showForm = !settings.loading && scriptLoaded;

  const handleComplete = (result) => {
    const flowState = `rebilly.complete.${result.payload.id}`;
    logInfo(sessionJWT, { flowLoc: "REBILLY_HANDLE_COMPLETE", flowState, result });
    setFlowState(flowState);
    onComplete(result.payload.id);
  };

  return (
    <>
      <Loader scriptUrl={settings.data.script_uri} onLoaded={setScriptLoaded} />
      {showSpinner && <FullPageSpinner data-testid="rebilly-wrapper-spinner" />}
      {showForm && (
        <RebillyForm
          rebillyLib={window.Rebilly}
          settings={settings.data}
          onReady={onReady}
          onComplete={handleComplete}
          onCancel={onCancel}
          config={config}
          hideCancelButton={hideCancelButton}
          requireTOS={requireTOS}
        />
      )}
    </>
  );
};
